import clsx from "clsx";
import { useId, useRef, useState } from "react";
import { GoogleReCaptcha } from "react-google-recaptcha-v3";

import { Checkbox, RadioButton } from "@afa-shared/afa-components";
import Button from "@afa-shared/afa-components/dist/Buttons/Button";
import Grid from "@afa-shared/afa-components/dist/Grid";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Select from "@afa-shared/afa-components/dist/Inputs/Select";
import TextInput from "@afa-shared/afa-components/dist/Inputs/TextInput";
import Text from "@afa-shared/afa-components/dist/Text";
import { FormProps } from "@components/FormBlock";
import { StandardLink } from "@components/StandardLink";

import FormErrorFeedback from "../Feedback/FormErrorFeedback";
import { FormSendFeedback } from "../Feedback/FormSendFeedback";
import FormSuccessFeedback from "../Feedback/FormSuccessFeedback";
import FormValidationFeedback from "../Feedback/FormValidationFeedback";
import formStyles from "../FormStyles/forms.module.css";
import { emailMaxInputLength, generalMaxInputLength, phoneMaxInputLength } from "../formConstants";
import { scrollIntoView, validate } from "../formHelper";

enum Gender {
  FEMALE = "Kvinna",
  MALE = "Man",
  OTHER = "Annat/vill inte svara",
}

enum EmploymentTypes {
  PERMANENT = "Fast anställning",
  FIXED_TERM = "Tidsbegränsad anställning",
  ON_DEMAND = "Behovsstyrd anställning/timanställd",
}

enum NativeTongue {
  YES = "Ja",
  NO = "Nej",
  OTHER = "Vill inte svara",
}

const CHECKBOX_OPTIONS = [
  { id: "isManagerWithStaff", label: "Jag är chef med personalansvar" },
  { id: "worksInHR", label: "Jag arbetar med HR/personalfrågor" },
  { id: "hasUnionAssignment", label: "Jag har ett fackligt uppdrag" },
  { id: "isInsuranceRepresentative", label: "Jag är försäkringsinformatör" },
  { id: "isLocalPolitician", label: "Jag är politiker inom kommun eller region" },
  { id: "isStudent", label: "Jag är student" },
  { id: "isRetired", label: "Jag är pensionär" },
  { id: "isUnemployed", label: "Jag är arbetssökande" },
];

type SelectOption = { id: string; name: string; value: string };

const EMPLOYER_OPTIONS: SelectOption[] = [
  { id: "region", name: "Region", value: "Region" },
  { id: "municipality", name: "Kommun", value: "Kommun" },
  { id: "municipalCompany", name: "Kommunalt bolag", value: "Kommunalt bolag" },
  { id: "tradeUnion", name: "Ett fackförbund", value: "Ett fackförbund" },
  {
    id: "multipleEmployersWithMunicipal",
    name: "Hos flera arbetsgivare, men minst en inom kommun/region",
    value: "Hos flera arbetsgivare, men minst en inom kommun/region",
  },
  { id: "privateCompany", name: "Privat företag", value: "Privat företag" },
  {
    id: "selfEmployed",
    name: "Jag är egenföretagare/frilansare/konsult",
    value: "Jag är egenföretagare/frilansare/konsult",
  },
  {
    id: "notEmployed",
    name: "Jag är inte anställd just nu",
    value: "Jag är inte anställd just nu",
  },
  {
    id: "other",
    name: "Annan beskrivning passar bättre",
    value: "Annan beskrivning passar bättre",
  },
];

export const KRWebForm = ({ contentfulPreviewEntryId }: FormProps) => {
  const [displayForm, setDisplayForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isValidToken, setIsValidToken] = useState(false);
  const [errorSendMail, setErrorSendMail] = useState(false);
  const [hasSingleError, setHasSingleError] = useState(false);

  // Form state: keep value + required flags in one place
  const [formState, setFormState] = useState({
    firstName: { value: "", required: true },
    lastName: { value: "", required: true },
    email: { value: "", required: true },
    phone: { value: "", required: true },
    location: { value: "", required: true },
    birth: { value: "", required: false, checkValidIfNotEmpty: true },
    role: { value: "", required: false },
    employer: { value: "", required: true },
    gender: { value: "", required: false },
    nativeTongue: { value: "", required: false },
    employmentType: { value: "", required: false },
    profiles: { value: [] as string[], required: false },
  });

  // Errors map: include all fields that can error/scroll
  const [errors, setErrors] = useState<Record<string, any>>({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    location: null,
    birth: null,
    role: null,
    employer: null,
    nativeTongue: null,
    gender: null,
    employmentType: null,
    profiles: null,
  });

  // Refs for focusing on error fields
  const formRefs = {
    sendFeedback: useRef<HTMLDivElement | null>(null),
    errorInfobox: useRef<HTMLDivElement | null>(null),
    firstName: useRef<HTMLInputElement | null>(null),
    lastName: useRef<HTMLInputElement | null>(null),
    email: useRef<HTMLInputElement | null>(null),
    location: useRef<HTMLInputElement | null>(null),
    role: useRef<HTMLInputElement | null>(null),
    employer: useRef<HTMLSelectElement | null>(null),
    birth: useRef<HTMLInputElement | null>(null),
    phone: useRef<HTMLInputElement | null>(null),
    nativeTongue: useRef<HTMLFieldSetElement | null>(null),
    gender: useRef<HTMLFieldSetElement | null>(null),
    employmentType: useRef<HTMLFieldSetElement | null>(null),
    profiles: useRef<HTMLFieldSetElement | null>(null),
  };

  const formIds = {
    firstName: useId(),
    lastName: useId(),
    email: useId(),
    phone: useId(),
    location: useId(),
    birth: useId(),
    role: useId(),
    employer: useId(),
    header: useId(),
    nativeTongue: useId(),
  };

  // Toggle a checkbox profile
  const handleCheckboxClick = (id: string) => {
    const exists = formState.profiles.value.includes(id);
    setFormState((prev) => ({
      ...prev,
      profiles: {
        value: exists ? prev.profiles.value.filter((v) => v !== id) : [...prev.profiles.value, id],
        required: false,
      },
    }));
    setErrors((prev) => ({ ...prev, profiles: null }));
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    key: keyof typeof formState
  ) => {
    const { value } = e.target;
    setFormState((prev) => ({ ...prev, [key]: { ...prev[key], value } }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // Radio setter (for non-event sources)
  const setFieldValue = (key: keyof typeof formState, value: any) => {
    setFormState((prev) => ({ ...prev, [key]: { ...prev[key], value } }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setHasSingleError(false);
    const formErrors = validate(formState as any, errors);
    setErrors(formErrors);
    const foundErrors = Object.values(formErrors).filter((v) => v !== null).length;

    if (foundErrors) {
      if (foundErrors === 1) {
        setHasSingleError(true);
        const key = Object.keys(formErrors).find((k) => formErrors[k] !== null)!;
        scrollIntoView(formRefs[key]);
      } else {
        scrollIntoView(formRefs.errorInfobox);
      }
      return;
    }

    const cleanFormState = Object.entries(formState).reduce<Record<string, any>>(
      (acc, [k, v]: [string, any]) => {
        //Profiles should use CHECKBOX_OPTIONS labels (Swedish naming)
        if (k === "profiles" && Array.isArray(v.value)) {
          acc[k] = v.value.map(
            (id: string) => CHECKBOX_OPTIONS.find((o) => o.id === id)?.label ?? id
          );
        } else {
          acc[k] = v.value;
        }
        return acc;
      },
      {}
    );

    setIsLoading(true);
    try {
      const res = await fetch("/api/kr-testpanel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanFormState),
      });

      if (res.ok) {
        setDisplayForm(true);
        formRefs?.sendFeedback?.current?.focus();
        // formTracker("inskickat", "testpanel");
      } else {
        setErrorSendMail(true);
        setDisplayForm(true);
        // formTracker("felmeddelande", "testpanel");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReCaptchaVerify = (token?: string) => {
    if (!token) return;
    setIsValidToken(true);
  };

  const renderErrorInfoBox = () => (
    <div ref={formRefs.errorInfobox}>
      <FormValidationFeedback
        formErrors={errors}
        formRefs={formRefs}
        hasSingleError={hasSingleError}
      />
    </div>
  );

  const renderFormFeedback = () => (
    <FormSuccessFeedback
      messageHeading="Tack!"
      messageChildren={
        <Text variant="paragraph" removeMargin>
          Din information har skickats.
        </Text>
      }
      messageLink={<StandardLink text="Så behandlar vi personuppgifter" url={"/personuppgifter"} />}
    />
  );

  const renderErrorMessage = () => <FormErrorFeedback />;

  const showFormFeedback = displayForm && !errorSendMail;
  const personuppgifterPath = "/personuppgifter";

  return (
    <Grid
      direction="column"
      className="form-container"
      data-contentful-entry-id={contentfulPreviewEntryId}
      data-contentful-field-id="name"
    >
      <GoogleReCaptcha onVerify={handleReCaptchaVerify} />

      <FormSendFeedback
        formSendRef={formRefs.sendFeedback}
        formErrorFeedback={renderErrorMessage()}
        formSuccessFeedback={renderFormFeedback()}
        showSuccessFeedback={showFormFeedback}
        showErrorFeedback={errorSendMail}
      />

      {!displayForm && (
        <div className={`${formStyles.formBackground} ${formStyles.formWrapper}`}>
          <div className={formStyles.formHeadingWrapper}>
            <Heading id={formIds.header} variant="h2" removeMargin>
              Intresseanmälan
            </Heading>
            <Text variant="paragraph" removeMargin>
              * Obligatoriskt fält
            </Text>
          </div>

          {renderErrorInfoBox()}

          <form aria-labelledby={formIds.header} noValidate className={formStyles.form}>
            <TextInput
              inputRef={formRefs.firstName}
              label="Ditt förnamn *"
              id={formIds.firstName}
              name="firstName"
              autoComplete="given-name"
              value={formState.firstName.value}
              type="text"
              maxLength={generalMaxInputLength}
              error={errors.firstName?.input}
              fullWidth
              onChange={(e) => handleFormChange(e, "firstName")}
            />

            <TextInput
              inputRef={formRefs.lastName}
              label="Ditt efternamn *"
              id={formIds.lastName}
              name="lastName"
              autoComplete="family-name"
              value={formState.lastName.value}
              type="text"
              maxLength={generalMaxInputLength}
              error={errors.lastName?.input}
              fullWidth
              onChange={(e) => handleFormChange(e, "lastName")}
            />

            <TextInput
              inputRef={formRefs.email}
              label="Din e-postadress *"
              id={formIds.email}
              name="email"
              autoComplete="email"
              value={formState.email.value}
              type="email"
              maxLength={emailMaxInputLength}
              width="100%"
              error={errors.email?.input}
              onChange={(e) => handleFormChange(e, "email")}
            />

            <TextInput
              inputRef={formRefs.phone}
              label="Ditt telefonnummer *"
              id={formIds.phone}
              name="phone"
              autoComplete="tel"
              width="100%"
              maxLength={phoneMaxInputLength}
              value={formState.phone.value}
              type="tel"
              error={errors.phone?.input}
              onChange={(e) => handleFormChange(e, "phone")}
            />

            <TextInput
              inputRef={formRefs.location}
              label="Bostadsort *"
              id={formIds.location}
              name="location"
              autoComplete="address-level2"
              value={formState.location.value}
              type="text"
              maxLength={generalMaxInputLength}
              width="100%"
              error={errors.location?.input}
              onChange={(e) => handleFormChange(e, "location")}
            />

            <TextInput
              inputRef={formRefs.birth}
              label="Ditt födelseår"
              description="Skriv ditt födelseår med fyra siffror"
              id={formIds.birth}
              name="birth"
              autoComplete="bday-year"
              width="100%"
              maxLength={4}
              minLength={2}
              value={formState.birth.value}
              type="number"
              error={errors.birth?.input}
              onChange={(e) => handleFormChange(e, "birth")}
            />

            <fieldset className={formStyles.radioWrapper} ref={formRefs.gender}>
              <div className={formStyles.radioWrapper__fieldSetHeader}>
                <legend>Kön</legend>
              </div>
              <RadioButton
                label="Kvinna"
                name="gender"
                value={Gender.FEMALE}
                onChange={() => setFieldValue("gender", Gender.FEMALE)}
                checked={formState.gender.value === Gender.FEMALE}
              />
              <RadioButton
                label="Man"
                name="gender"
                value={Gender.MALE}
                onChange={() => setFieldValue("gender", Gender.MALE)}
                checked={formState.gender.value === Gender.MALE}
              />
              <RadioButton
                label="Annat/vill inte svara"
                name="gender"
                value={Gender.OTHER}
                onChange={() => setFieldValue("gender", Gender.OTHER)}
                checked={formState.gender.value === Gender.OTHER}
              />
            </fieldset>

            <fieldset className={formStyles.radioWrapper} ref={formRefs.nativeTongue}>
              <div className={formStyles.radioWrapper__fieldSetHeader}>
                <legend>Är svenska ditt modersmål?</legend>
              </div>

              <RadioButton
                label="Ja"
                name="nativeTongue"
                value={NativeTongue.YES}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.YES)}
                checked={formState.nativeTongue.value === NativeTongue.YES}
              />
              <RadioButton
                label="Nej"
                name="nativeTongue"
                value={NativeTongue.NO}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.NO)}
                checked={formState.nativeTongue.value === NativeTongue.NO}
              />
              <RadioButton
                label="Vill inte svara"
                name="nativeTongue"
                value={NativeTongue.OTHER}
                onChange={() => setFieldValue("nativeTongue", NativeTongue.OTHER)}
                checked={formState.nativeTongue.value === NativeTongue.OTHER}
              />
            </fieldset>

            <TextInput
              inputRef={formRefs.role}
              label="Vad har du för yrke/roll?"
              id={formIds.role}
              name="role"
              width="100%"
              maxLength={generalMaxInputLength}
              value={formState.role.value}
              type="text"
              error={errors.role?.input}
              onChange={(e) => handleFormChange(e, "role")}
            />

            <Select
              inputRef={formRefs.employer}
              labelText="Var du är anställd *"
              options={EMPLOYER_OPTIONS}
              id={formIds.employer}
              selected={formState.employer.value}
              placeholder="Välj arbetsgivare"
              error={errors.employer?.input}
              onChange={(e) => handleFormChange(e as any, "employer")}
              width="100%"
            />

            <fieldset className={formStyles.radioWrapper} ref={formRefs.employmentType}>
              <div className={formStyles.radioWrapper__fieldSetHeader}>
                <legend>Vad har du för typ av anställning?</legend>
              </div>

              <RadioButton
                label="Fast anställning"
                name="employmentType"
                value={EmploymentTypes.PERMANENT}
                onChange={() => setFieldValue("employmentType", EmploymentTypes.PERMANENT)}
                checked={formState.employmentType.value === EmploymentTypes.PERMANENT}
              />
              <RadioButton
                label="Tidsbegränsad anställning"
                name="employmentType"
                value={EmploymentTypes.FIXED_TERM}
                onChange={() => setFieldValue("employmentType", EmploymentTypes.FIXED_TERM)}
                checked={formState.employmentType.value === EmploymentTypes.FIXED_TERM}
              />
              <RadioButton
                label="Behovsstyrd anställning/timanställd"
                name="employmentType"
                value={EmploymentTypes.ON_DEMAND}
                onChange={() => setFieldValue("employmentType", EmploymentTypes.ON_DEMAND)}
                checked={formState.employmentType.value === EmploymentTypes.ON_DEMAND}
              />
            </fieldset>

            <fieldset className={formStyles.radioWrapper} ref={formRefs.profiles}>
              <div className={formStyles.radioWrapper__fieldSetHeader}>
                <legend>Har du någon av följande profiler?</legend>
                <p>Om inget av detta stämmer in på dig kan du hoppa över frågan</p>
              </div>
              {CHECKBOX_OPTIONS.map((option) => (
                <Checkbox
                  key={option.id}
                  id={option.id}
                  label={option.label}
                  onChange={() => handleCheckboxClick(option.id)}
                  checked={formState.profiles.value.includes(option.id)}
                />
              ))}
            </fieldset>

            <StandardLink url={personuppgifterPath} text="Så behandlar vi personuppgifter" />

            <Button
              type="submit"
              buttonType="primary"
              onClick={isValidToken ? onSubmit : (e) => e.preventDefault()}
              label={isLoading ? "" : "Skicka"}
              className={clsx(formStyles.submitButton, { [formStyles.spinner]: isLoading })}
              disabled={isLoading}
            />
          </form>
        </div>
      )}
    </Grid>
  );
};

export default KRWebForm;
